import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CreationStatus } from 'redux/middleware/api/creation-service/types';
import {
  cancelWaitForCreation,
  waitForClipSuggestionsCreation,
} from 'redux/modules/wizard-export/actions';
import { creationIdSelector } from 'redux/modules/wizard-export/selectors';
import { Dispatch } from 'redux/types';
import ExportVideoStepContainer from '../ExportVideoStep/ExportVideoStepContainer';
import ExportVideoStepMainSection from '../ExportVideoStep/ExportVideoStepMainSection';
import ProgressBlock from '../ExportVideoStep/ProgressBlock';
import useCreationSuggestionsProgress, {
  UseCreationSuggestionsProgressConfig,
} from './useCreationSuggestionsProgress';

export interface VideoWizardClipsExportStepProps
  extends Pick<UseCreationSuggestionsProgressConfig, 'videoFileSize'> {
  onError?: (error: Error) => void;
}

const VideoWizardClipsExportStep: React.FC<VideoWizardClipsExportStepProps> = ({
  onError,
  videoFileSize,
}) => {
  const creationId = useSelector(creationIdSelector);
  const dispatch = useDispatch<Dispatch>();

  const {
    progress,
    estimatedCompletedAt,
    hasNoProgressEstimate,
  } = useCreationSuggestionsProgress({ creationId, videoFileSize });

  useEffect(() => {
    const waitForExport = async () => {
      try {
        await dispatch(
          waitForClipSuggestionsCreation(
            creationStatus => creationStatus === CreationStatus.COMPLETED,
          ),
        );
      } catch (error) {
        onError(error);
      }
    };

    if (creationId) {
      waitForExport();
    }

    return () => {
      dispatch(cancelWaitForCreation());
    };
  }, [creationId, dispatch, onError]);

  return (
    <ExportVideoStepContainer>
      <ExportVideoStepMainSection>
        <ProgressBlock
          exportMessage={
            hasNoProgressEstimate
              ? "We're uploading your file"
              : 'Your video clips should be ready soon'
          }
          subtitle={
            hasNoProgressEstimate
              ? "This may take a few minutes, please don't refresh"
              : "You don't need to wait here, we'll email it to you soon."
          }
          layout="vertical"
          estimatedCompletedAtMillis={estimatedCompletedAt}
          showEmailCallout={false}
          progress={progress}
        />
      </ExportVideoStepMainSection>
    </ExportVideoStepContainer>
  );
};

export default VideoWizardClipsExportStep;
